// src/views/AuthPage.js
import React, { useState } from 'react';
import { signInWithEmailAndPassword, signInWithPopup, signOut, sendPasswordResetEmail } from "firebase/auth";
import { auth, microsoftProvider, googleProvider, db } from '../firebase/config'; 
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore'; 
import { useNavigate } from 'react-router-dom'; 
import { Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import logo from '../assets/logo.jpg'; // Ensuring this path is correct

const AuthPage = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [resetMessage, setResetMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    // Helper to register the session
    const registerSession = async (user) => {
        const sessionId = Date.now().toString(); 
        
        const userRef = doc(db, 'users', user.uid);
        const userSnap = await getDoc(userRef);

        // --- THE GATEKEEPER ---
        if (!userSnap.exists()) {
            console.warn("⛔ No profile found. Redirecting to Pricing.");
            await signOut(auth);
            navigate('/pricing'); 
            return false; 
        }

        // If profile exists, proceed with session stamping
        localStorage.setItem('crm_web_session_id', sessionId);
        await setDoc(userRef, {
            currentWebSessionId: sessionId,
            lastLoginAt: serverTimestamp()
        }, { merge: true });

        return true; 
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        setResetMessage('');
        setLoading(true);

        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const success = await registerSession(userCredential.user);
            if (success) {
                navigate('/'); 
            }
        } catch (err) {
            console.error("❌ Login Failed:", err);
            setError("Invalid email or password. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleSocialLogin = async (provider) => {
        setError('');
        setResetMessage('');
        try {
            const result = await signInWithPopup(auth, provider);
            const success = await registerSession(result.user);
            if (success) {
                navigate('/');
            }
        } catch (error) {
            console.error("Social Sign-In Error", error);
            setError("Unable to sign in with that provider. Please try again.");
        }
    };

    const handlePasswordReset = async (e) => {
        e.preventDefault();
        setError('');
        setResetMessage('');
        
        if (!email) {
            setError('Please enter your email address in the field below first to reset your password.');
            return;
        }

        setLoading(true);
        try {
            await sendPasswordResetEmail(auth, email);
            setResetMessage('Password reset email sent! Please check your inbox.');
        } catch (error) {
            console.error("Password Reset Error", error);
            setError("Failed to send password reset email. Please verify your email address.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen w-full flex bg-white font-sans text-gray-900">
            
            {/* --- LEFT SIDE: BRANDING (Desktop Only) --- */}
            <div className="hidden lg:flex w-1/2 bg-gradient-to-br from-indigo-900 via-purple-800 to-orange-500 relative overflow-hidden items-center justify-center p-12">
                {/* Abstract Background Shapes */}
                <div className="absolute top-0 left-0 w-full h-full opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
                <div className="absolute -top-24 -left-24 w-96 h-96 bg-purple-600 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-pulse"></div>
                <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-orange-600 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-pulse"></div>

                <div className="relative z-10 max-w-lg text-white">
                    {/* LOGO LOCKUP: Side-by-Side but Larger */}
                    <div className="mb-10 flex items-center gap-6">
                        {/* Solid white box, increased size to w-24 h-24 (96px) */}
                        <div className="w-24 h-24 bg-white rounded-2xl flex items-center justify-center shadow-2xl p-3 shrink-0">
                            <img src={logo} alt="Spatchy Logo" className="w-full h-full object-contain" />
                        </div>
                        {/* Text scaled up to match */}
                        <span className="text-6xl font-bold tracking-tight drop-shadow-md">Spatchy</span>
                    </div>

                    <h1 className="text-5xl font-extrabold mb-6 leading-tight drop-shadow-sm">
                        Master your business.
                    </h1>
                    <p className="text-xl text-purple-100 font-light leading-relaxed drop-shadow-sm">
                        From the first lead to the final invoice, run your entire business from anywhere in the world.
                    </p>
                </div>
            </div>

            {/* --- RIGHT SIDE: LOGIN FORM --- */}
            <div className="w-full lg:w-1/2 flex flex-col justify-center items-center p-6 md:p-12 relative">
                
                <div className="w-full max-w-md space-y-8">
                    {/* Mobile Logo (Visible only on small screens) */}
                    <div className="lg:hidden flex flex-col items-center mb-8">
                        <div className="w-20 h-20 bg-white rounded-2xl shadow-md p-3 mb-3 border border-gray-100">
                             <img src={logo} alt="Spatchy Logo" className="w-full h-full object-contain" />
                        </div>
                        <h1 className="text-3xl font-extrabold text-gray-900">
                            Spatchy
                        </h1>
                    </div>

                    <div className="text-center lg:text-left">
                        <h2 className="text-3xl font-bold tracking-tight text-gray-900">Welcome back</h2>
                        <p className="mt-2 text-sm text-gray-500">
                            Please enter your details to sign in.
                        </p>
                    </div>

                    <div className="space-y-4">
                        {/* Social Login Buttons */}
                        <div className="flex flex-col gap-3">
                            <button
                                onClick={() => handleSocialLogin(microsoftProvider)}
                                className="flex items-center justify-center gap-2 px-4 py-3 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all shadow-sm w-full"
                            >
                                <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="currentColor" d="M11.4,24H0V12.6h11.4V24z M24,24H12.6V12.6H24V24z M11.4,11.4H0V0h11.4V11.4z M24,11.4H12.6V0H24V11.4z"/></svg>
                                Sign in with Microsoft
                            </button>
                        </div>

                        <div className="relative">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-gray-200"></div>
                            </div>
                            <div className="relative flex justify-center text-sm">
                                <span className="px-2 bg-white text-gray-500">Or continue with email</span>
                            </div>
                        </div>

                        {/* Email Form */}
                        <form onSubmit={handleLogin} className="space-y-5">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Email address</label>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all"
                                    placeholder="name@company.com"
                                    required
                                />
                            </div>

                            <div>
                                <div className="flex justify-between items-center mb-1">
                                    <label className="block text-sm font-medium text-gray-700">Password</label>
                                    <a href="#" onClick={handlePasswordReset} className="text-xs font-semibold text-blue-600 hover:text-blue-500">Forgot password?</a>
                                </div>
                                <input
                                    type="password"
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all"
                                    placeholder="••••••••"
                                    
                                />
                            </div>
                            
                            {error && (
                                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-600 text-sm animate-in fade-in slide-in-from-top-2">
                                    <AlertCircle size={16} />
                                    {error}
                                </div>
                            )}

                            {resetMessage && (
                                <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-600 text-sm animate-in fade-in slide-in-from-top-2">
                                    <CheckCircle size={16} />
                                    {resetMessage}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full flex items-center justify-center gap-2 py-3.5 px-4 bg-gray-900 hover:bg-black text-white text-sm font-bold rounded-lg shadow-lg hover:shadow-xl transition-all active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed"
                            >
                                {loading ? <Loader2 className="animate-spin" size={20} /> : 'Sign in to account'}
                            </button>
                        </form>
                    </div>

                    <p className="text-center text-sm text-gray-500">
                        Don't have an account?{' '}
                        <button 
                            onClick={() => navigate('/pricing')} 
                            className="font-bold text-blue-600 hover:text-blue-500 hover:underline"
                        >
                            Start free trial
                        </button>
                    </p>
                </div>
                
                {/* Copyright Footer */}
                <div className="absolute bottom-6 text-xs text-gray-400">
                    © {new Date().getFullYear()} Spatchy. All rights reserved.
                </div>
            </div>
        </div>
    );
};

export default AuthPage;